import { useEffect, useState, useCallback, type ReactNode, type SyntheticEvent, type MouseEventHandler } from 'react';
import { Children } from 'react';
// -*- mode: jsx;-*-
import { KeyboardClickable } from './KeyboardClickable.js';
import { Svg } from './Svg.js';
import { SHARE_TOOLS, trackShareToolClick } from './shareToolUtils.js';
import { appendToTrackingLinkRF } from './shareBarUtils';
import { setUpOptimizelyListener, useOptimizelyVariant } from './optimizely/OptimizelyTesting';

export const safeNextdoorURL = (body:string, link:string) => {
    // default to the event driven share body
    body = shareBody || body;
    // Add spaces so Nextdoor doesn't assume they are framing a url :eyeroll:
    body = body.replace(/(\S+)\.(\S+)/g, '$1. $2');
    // Add a hashcode if set
    const shareTag = hashtag ? `&hashtag=${hashtag}` : '';
    // Append any A/B test rfs
    link = appendToTrackingLinkRF(link, nextdoorRF);
    return `https://nextdoor.com/sharekit/?source=DonorsChoose${shareTag}&body=${encodeURIComponent(body)}` + link;
};

// All of the link logic lives in this class.
// We listen also in `shareBarOnClickHandlers` to retrigger `setNextdoorLink` to pick up the changes.
export const NEXTDOOR_EVENT = 'nd-36268';
let [nextdoorRF, shareBody, hashtag] = [null, null, 'fundraising'];

interface NextDoorEvent extends CustomEvent {
    rf?: string
    body?: string
    hashtag?: string
}

setUpOptimizelyListener<NextDoorEvent>(NEXTDOOR_EVENT, (event) => {
    console.log('Nextdoor A/B test update', event.detail);
    nextdoorRF = event.detail.rf;
    shareBody = event.detail.body;
    hashtag = event.detail.hashtag;
});

interface NextdoorLinkProps {
    urlToShare: string,
    pageName: string,
    getTrackingLink: typeof window.getTrackingLink,
    useSpan?: boolean,
    className?: string,
    closeAfterShare?: boolean,
    onNextdoorClick: (event:SyntheticEvent, url:string) => MouseEventHandler,
    children?: ReactNode,
    teacherName: string,
    schoolName: string,
    projectSummary: string,
    projectId?: number,
    essentialsListShare?: boolean
}

export const NextdoorLink = ({
    useSpan, pageName, getTrackingLink, urlToShare, className, closeAfterShare = false, onNextdoorClick, children,
    teacherName, schoolName, projectSummary, projectId, essentialsListShare,
}:NextdoorLinkProps) => {
    const getNextdoorUrl = useCallback(() => {
        const intro = `Hi! I'm ${teacherName}, a teacher at ${schoolName} and a neighbor here.`;
        const summary = projectSummary;
        const outro = 'Support my students here: ';
        const body = `${intro}\n\n${summary}\n\n${outro}`;
        const paramLinks = getTrackingLink(urlToShare, 'nextdoor', pageName);
        return safeNextdoorURL(body, paramLinks);
    }, [getTrackingLink, pageName, projectSummary, schoolName, teacherName, urlToShare]);

    const [shareUrl, setShareUrl] = useState(getNextdoorUrl());

    useOptimizelyVariant<NextDoorEvent>(
            NEXTDOOR_EVENT,
            () => { setTimeout(() => setShareUrl(getNextdoorUrl()), 20) },
            null,
            [getNextdoorUrl]
    );

    const shareIcon = () => {
        return Children.count(children) > 0 ? null : <Svg icon="ss-nextdoor" iconFamily="social-circle"/>;
    };

    const onClick = (event:SyntheticEvent):MouseEventHandler => {
        window.dcAnalytics.trackShareToolClick(pageName, urlToShare, 'Nextdoor', essentialsListShare);
        trackShareToolClick(projectId, SHARE_TOOLS.NEXTDOOR);
        return onNextdoorClick(event, getNextdoorUrl());
    };

    return useSpan
        ? <KeyboardClickable>
            <span tabIndex={0}
                onClick={onClick}
                // Key press handled by KeyboardClickable
                onKeyDown={() => {}}
                role='button'
                aria-label="Share on Nextdoor"
                className={className + ' heap-nextdoor-share'}>
                {shareIcon()}
                {children}
            </span>
        </KeyboardClickable>
        : <a href={shareUrl}
            onClick={onClick}
            target="_blank"
            aria-label="Share on Nextdoor"
            className={className + ' heap-nextdoor-share'} rel="noreferrer">
            {shareIcon()}
            {children}
        </a>;
};
